import React from 'react';
import { FiBriefcase, FiMapPin, FiClock } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGridThree,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  CheckboxGroup,
  CheckboxLabel,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText
} from '../common/FormStyles';
import { Candidate } from '@/models';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { INFORMAL_ACTIVITY_DOCUMENTS } from '@/config/documentsBySection';

interface InformalActivitySectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
}

export const InformalActivitySection: React.FC<InformalActivitySectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleLocationToggle = (type: string, checked: boolean) => {
    const currentLocations = data.lieux_activite || [];
    const newLocations = checked
      ? [...currentLocations, type as any]
      : currentLocations.filter(l => l !== type);
    handleChange('lieux_activite', newLocations);
  };

  const handleDayToggle = (day: string, checked: boolean) => {
    const currentDays = data.jours_travail || [];
    const newDays = checked
      ? [...currentDays, day as any]
      : currentDays.filter(d => d !== day);
    handleChange('jours_travail', newDays);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiBriefcase />
        Votre Activité
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Décrivez votre activité professionnelle. Ces informations nous aident à comprendre
          votre situation et vérifier la régularité de vos revenus.
        </p>
      </InfoBox>

      <SubSectionTitle>Description de votre travail</SubSectionTitle>

      <FormGroup>
        <Label $required>Qu'est-ce que vous faites comme activité principale ?</Label>
        <Input
          type="text"
          value={data.activite_principale || ''}
          onChange={e => handleChange('activite_principale', e.target.value)}
          placeholder="Ex: Couture, Coiffure, Vente, Mécanique, Menuiserie, Taxi-moto, Restauration..."
          disabled={readonly}
          list="activite-suggestions"
        />
        <datalist id="activite-suggestions">
          <option value="Couture" />
          <option value="Coiffure" />
          <option value="Vente de produits" />
          <option value="Mécanique automobile" />
          <option value="Menuiserie" />
          <option value="Taxi-moto" />
          <option value="Restauration" />
          <option value="Électricité" />
          <option value="Plomberie" />
          <option value="Maçonnerie" />
          <option value="Agriculture" />
          <option value="Élevage" />
          <option value="Transport" />
          <option value="Réparation électronique" />
          <option value="Pâtisserie" />
        </datalist>
        <HelpText>Décrivez votre métier ou commerce principal</HelpText>
      </FormGroup>

      <FormGroup>
        <Label $required>Depuis combien de temps faites-vous cette activité ?(années)</Label>
        <Input
            type="number"
            min="1"
            value={data.duree_activite || ''}
            onChange={e => handleChange('duree_activite', parseInt(e.target.value))}
            placeholder="Ex: 15"
            disabled={readonly}
          />
        
      </FormGroup>

      {/* {data.duree_activite === 'Plus de 10 ans' && (
        <FormGroup>
          <Label>Précisez le nombre d'années</Label>
          <Input
            type="number"
            min="10"
            value={data.nombre_annees_si_plus_10 || ''}
            onChange={e => handleChange('nombre_annees_si_plus_10', parseInt(e.target.value))}
            placeholder="Ex: 15"
            disabled={readonly}
          />
          <HelpText>Nombre d'années d'expérience dans cette activité</HelpText>
        </FormGroup>
      )} */}

      <SubSectionTitle>
        <FiMapPin style={{ fontSize: '18px' }} />
        Lieu d'exercice de l'activité
      </SubSectionTitle>

      <FormGroup>
        <Label $required>Où exercez-vous votre activité ?</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('À domicile')}
              onChange={e => handleLocationToggle('À domicile', e.target.checked)}
              disabled={readonly}
            />
            À domicile
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('Au marché')}
              onChange={e => handleLocationToggle('Au marché', e.target.checked)}
              disabled={readonly}
            />
            Au marché
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('Dans la rue/Ambulant')}
              onChange={e => handleLocationToggle('Dans la rue/Ambulant', e.target.checked)}
              disabled={readonly}
            />
            Dans la rue / Ambulant
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('Chez les clients')}
              onChange={e => handleLocationToggle('Chez les clients', e.target.checked)}
              disabled={readonly}
            />
            Chez les clients
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('Atelier/Local')}
              onChange={e => handleLocationToggle('Atelier/Local', e.target.checked)}
              disabled={readonly}
            />
            Atelier / Local
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.lieux_activite || []).includes('Autre')}
              onChange={e => handleLocationToggle('Autre', e.target.checked)}
              disabled={readonly}
            />
            Autre
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs lieux</HelpText>
      </FormGroup>

      {(data.lieux_activite || []).includes('Au marché') && (
        <FormGroup>
          <Label>Nom du marché</Label>
          <Input
            type="text"
            value={data.nom_marche || ''}
            onChange={e => handleChange('nom_marche', e.target.value)}
            placeholder="Ex: Marché de Cocody, Marché d'Adjamé..."
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.lieux_activite || []).includes('Dans la rue/Ambulant') && (
        <FormGroup>
          <Label>Quartiers parcourus</Label>
          <Input
            type="text"
            value={data.quartiers_parcourus || ''}
            onChange={e => handleChange('quartiers_parcourus', e.target.value)}
            placeholder="Ex: Yopougon, Abobo, Koumassi..."
            disabled={readonly}
          />
          <HelpText>Indiquez les quartiers où vous travaillez habituellement</HelpText>
        </FormGroup>
      )}

      {(data.lieux_activite || []).includes('Atelier/Local') && (
        <FormGroup>
          <Label>Quartier de l'atelier/local</Label>
          <Input
            type="text"
            value={data.quartier_atelier || ''}
            onChange={e => handleChange('quartier_atelier', e.target.value)}
            placeholder="Ex: Marcory Zone 4"
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.lieux_activite || []).includes('Autre') && (
        <FormGroup>
          <Label>Précisez le lieu</Label>
          <Input
            type="text"
            value={data.autre_lieu_details || ''}
            onChange={e => handleChange('autre_lieu_details', e.target.value)}
            placeholder="Décrivez où vous travaillez"
            disabled={readonly}
          />
        </FormGroup>
      )}

      <SubSectionTitle>Organisation du travail</SubSectionTitle>

      <FormGroup>
        <Label $required>Travaillez-vous :</Label>
        <Select
          value={data.mode_travail || ''}
          onChange={e => handleChange('mode_travail', e.target.value)}
          disabled={readonly}
        >
          <option value="">Sélectionnez...</option>
          <option value="Seul(e)">Seul(e)</option>
          <option value="Avec famille">Avec des membres de ma famille</option>
          <option value="Avec apprentis">Avec des apprentis</option>
          <option value="Avec employés">Avec des employés</option>
        </Select>
      </FormGroup>

      {data.mode_travail && data.mode_travail !== 'Seul(e)' && (
        <FormGroup>
          <Label>Nombre de personnes qui travaillent avec vous</Label>
          <Input
            type="number"
            min="1"
            value={data.nombre_employes_permanents || ''}
            onChange={e => handleChange('nombre_employes_permanents', parseInt(e.target.value))}
            placeholder="Ex: 3"
            disabled={readonly}
          />
        </FormGroup>
      )}

      <SubSectionTitle>
        <FiClock style={{ fontSize: '18px' }} />
        Horaires de travail
      </SubSectionTitle>

      <FormGrid>
        <FormGroup>
          <Label>Heure de début</Label>
          <Input
            type="time"
            value={data.heure_debut || ''}
            onChange={e => handleChange('heure_debut', e.target.value)}
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Heure de fin</Label>
          <Input
            type="time"
            value={data.heure_fin || ''}
            onChange={e => handleChange('heure_fin', e.target.value)}
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Jours de travail</Label>
        <HelpText style={{ marginBottom: '12px' }}>
          Sélectionnez vos jours de travail habituels. Cette information sera utilisée pour calculer vos revenus mensuels.
        </HelpText>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Lundi')}
              onChange={e => handleDayToggle('Lundi', e.target.checked)}
              disabled={readonly}
            />
            Lun
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Mardi')}
              onChange={e => handleDayToggle('Mardi', e.target.checked)}
              disabled={readonly}
            />
            Mar
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Mercredi')}
              onChange={e => handleDayToggle('Mercredi', e.target.checked)}
              disabled={readonly}
            />
            Mer
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Jeudi')}
              onChange={e => handleDayToggle('Jeudi', e.target.checked)}
              disabled={readonly}
            />
            Jeu
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Vendredi')}
              onChange={e => handleDayToggle('Vendredi', e.target.checked)}
              disabled={readonly}
            />
            Ven
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Samedi')}
              onChange={e => handleDayToggle('Samedi', e.target.checked)}
              disabled={readonly}
            />
            Sam
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.jours_travail || []).includes('Dimanche')}
              onChange={e => handleDayToggle('Dimanche', e.target.checked)}
              disabled={readonly}
            />
            Dim
          </CheckboxLabel>
        </CheckboxGroup>
      </FormGroup>

      <SubSectionTitle>Autres activités</SubSectionTitle>

      <FormGroup>
        <Label>Avez-vous d'autres activités pour gagner de l'argent ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.a_activites_secondaires || false}
            onChange={e => handleChange('a_activites_secondaires', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.a_activites_secondaires ? 'Oui, j\'ai d\'autres activités' : 'Non, une seule activité'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.a_activites_secondaires && (
        <>
          <FormGroup>
            <Label>Activités secondaires</Label>
            <TextArea
              value={data.activites_secondaires || ''}
              onChange={e => handleChange('activites_secondaires', e.target.value)}
              placeholder="Décrivez vos autres activités..."
              rows={3}
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Revenus mensuels de ces activités (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.autres_revenus || ''}
              onChange={e => handleChange('autres_revenus', parseInt(e.target.value))}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}


      {onDocumentUpload && onDocumentRemove && (
                  <DocumentUpload
                    sectionTitle="Votre Activité"
                    documents={INFORMAL_ACTIVITY_DOCUMENTS}
                    uploadedFiles={uploadedDocuments}
                    onFileSelect={onDocumentUpload}
                    onFileRemove={onDocumentRemove}
                    readonly={readonly}
                  />
                )}
    </FormSection>
  );
};
